'use client';

import React from 'react';
import type {
  CustomerAmount,
  CustomerStatusFinancialDetails,
} from '@packages/gql/generated/shopping/graphql';
import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import { FormattedMessage } from 'react-intl';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Price } from '@packages/shared/src/ui/Price/Price';
import { PriceValue } from '@packages/shared/src/ui/Price/PriceValue/PriceValue';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { Chip } from '@packages/shared/src/ui/Chip/Chip';

interface Props {
  balance: CustomerAmount;
  dueAmount: CustomerAmount | null;
  nextMaturityDate: string | null;
  financialDetails: CustomerStatusFinancialDetails | null;
  className?: string;
  cardHeadline: React.ReactNode;
  cardFooter?: React.ReactNode;
}
export const Maturities: React.FC<Props> = ({
  balance,
  dueAmount,
  nextMaturityDate,
  financialDetails,
  className,
  cardHeadline,
  cardFooter,
}) => {
  const { formatPrice } = useFormatPrice();
  const paymentDueDate = financialDetails?.minimumRateDueDate || nextMaturityDate;
  const paymentDueSum = financialDetails?.totalMinimumRate || dueAmount?.value || 0;

  return (
    <Card className={className} hasAutoSeparation>
      <CardHeader hasDivider>{cardHeadline}</CardHeader>
      <CardContent className="text-size-body-sm pb-2">
        <div className="flex justify-between">
          <div className="font-bold">
            <FormattedMessage id="Maturities.AccountBalance" defaultMessage="Dein Kontostand" />
          </div>
          <div>
            <Price size="small" alignment="right">
              <PriceValue>{formatPrice(balance.value, balance.currencyCode)}</PriceValue>
            </Price>
            {balance.value < 0 && (
              <Chip variant="filled" size="small" color="error" className="ml-2">
                <span className="px-1 py-0.25">
                  <FormattedMessage
                    id="Maturities.BalanceNegative"
                    defaultMessage="OFFENER SALDO"
                  />
                </span>
              </Chip>
            )}
            {balance.value > 0 && (
              <Chip variant="filled" size="small" color="success" className="ml-2">
                <span className="px-1 py-0.25">
                  <FormattedMessage id="Maturities.BalancePositive" defaultMessage="Guthaben" />
                </span>
              </Chip>
            )}
            {balance.value === 0 && (
              <div className="font-bold">
                <FormattedMessage
                  id="Maturities.BalanceZero"
                  defaultMessage="Dein Konto ist ausgeglichen"
                />
              </div>
            )}
          </div>
        </div>
        {financialDetails && (
          <div className="border-color-grey my-1.5 rounded-md border p-1.5">
            <div className="flex justify-between">
              <div className="font-bold">
                <FormattedMessage
                  id="Maturities.Financials.totalMinimumRate"
                  defaultMessage="Fälliger Betrag"
                />
              </div>
              <div className="font-bold">
                <Price size="small">
                  <PriceValue>
                    {formatPrice(financialDetails.totalMinimumRate, balance.currencyCode)}
                  </PriceValue>
                </Price>
              </div>
            </div>
            <Divider className="my-0.5" />
            <div className="flex justify-between">
              <div>
                <FormattedMessage
                  id="Maturities.Financials.minimumRate"
                  defaultMessage="Mindestrate"
                />
              </div>
              <div>
                <Price size="small">
                  <PriceValue className="font-normal">
                    {formatPrice(financialDetails.minimumRate, balance.currencyCode)}
                  </PriceValue>
                </Price>
              </div>
            </div>
            <div className="flex justify-between">
              <div>
                <FormattedMessage
                  id="Maturities.Financials.rdiAmount"
                  defaultMessage="Aktuelle Prämie Zahlschutz"
                />
              </div>
              <div>
                <Price size="small">
                  <PriceValue className="font-normal">
                    {formatPrice(financialDetails.rdiAmount, balance.currencyCode)}
                  </PriceValue>
                </Price>
              </div>
            </div>
          </div>
        )}
        {paymentDueSum > 0 && (
          <>
            <Divider className="my-1.5" />
            <div className="mb-0.5">
              <FormattedMessage
                id="Maturities.paymentRequest"
                defaultMessage="Bitte überweise {amount}{date}."
                values={{
                  amount: (
                    <Price size="small" className="inline">
                      <PriceValue>{formatPrice(paymentDueSum, balance.currencyCode)}</PriceValue>
                    </Price>
                  ),
                  date: paymentDueDate ? (
                    <>
                      {' '}
                      <FormattedMessage
                        id="Maturities.paymentRequestDate"
                        defaultMessage="bis zum {date}"
                        values={{
                          date: <span className="font-bold">{paymentDueDate}</span>,
                        }}
                      />
                    </>
                  ) : (
                    ''
                  ),
                }}
              />
            </div>
            <div className="text-size-body-xs text-color-text-darktransparent">
              <FormattedMessage
                id="Maturities.paymentWorkinngHint"
                defaultMessage="Wenn du deine Buchung vor wenigen Tagen getätigt hast, sind wir bereits dabei, diese zu bearbeiten. Habe bitte etwas Geduld."
              />
            </div>
          </>
        )}
      </CardContent>
      {cardFooter}
    </Card>
  );
};
